import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { ArrowLeft, Leaf, MapPin, TreePine, BadgeDollarSign, Building, FileText, Calendar, Clock, Gavel, CheckCircle, DollarSign, X, CreditCard, FileBarChart, History, Tag, Phone, Mail, Globe, CircleDollarSign } from 'lucide-react';

interface ProjetoOfertante {
  id: string;
  nome: string;
  tipo: string;
  regiao: string;
  creditosGerados: number;
  statusVerificacao: 'Verificado' | 'Em análise' | 'Pendente';
  descricao: string;
  dataInicio: string;
  dataFim: string;
}

interface EmpresaCompradora {
  id: string;
  nome: string;
  setor: string;
  creditosAdquiridos: number;
  ultimaCompra: string;
}

// Interface estendida para adicionar dados detalhados da empresa
interface EmpresaDetalhes extends EmpresaCompradora {
  cnpj?: string;
  endereco?: string;
  telefone?: string;
  email?: string;
  website?: string;
  historico?: {
    data: string;
    creditos: number;
    valor: number;
    projeto: string;
  }[];
}

const ProjetoOfertanteDetalhe: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [projeto, setProjeto] = useState<ProjetoOfertante | null>(null);
  const [empresasCompradoras, setEmpresasCompradoras] = useState<EmpresaDetalhes[]>([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<EmpresaDetalhes | null>(null);
  const [modalAberto, setModalAberto] = useState<boolean>(false);
  
  // Mock de dados para o projeto específico
  useEffect(() => {
    // Em um cenário real, aqui você faria uma chamada à API para buscar os dados do projeto pelo ID
    // Simulando um projeto para demonstração
    const mockProjeto: ProjetoOfertante = {
      id: 'PRJ-2023-001',
      nome: 'Reflorestamento Vale do Ribeira',
      tipo: 'Florestal',
      regiao: 'Sudeste',
      creditosGerados: 15000,
      statusVerificacao: 'Verificado',
      descricao: 'Projeto de reflorestamento que visa recuperar áreas degradadas no Vale do Ribeira, contribuindo para a captura de carbono e preservação da biodiversidade local.',
      dataInicio: '2023-03-15',
      dataFim: '2025-03-15'
    };
    
    if (id === 'PRJ-2023-002') {
      mockProjeto.id = 'PRJ-2023-002';
      mockProjeto.nome = 'Energia Solar Sertão Nordestino';
      mockProjeto.tipo = 'Energia Renovável';
      mockProjeto.regiao = 'Nordeste';
      mockProjeto.creditosGerados = 8500;
      mockProjeto.descricao = 'Projeto de geração de energia limpa através de painéis solares instalados no sertão nordestino, reduzindo a emissão de gases de efeito estufa.';
    } else if (id === 'PRJ-2023-003') {
      mockProjeto.id = 'PRJ-2023-003';
      mockProjeto.nome = 'Preservação Manguezais Costeiros';
      mockProjeto.tipo = 'Costeiro';
      mockProjeto.regiao = 'Sul';
      mockProjeto.creditosGerados = 12000;
      mockProjeto.statusVerificacao = 'Em análise';
      mockProjeto.descricao = 'Projeto de preservação de áreas de manguezais na costa brasileira, ecossistemas com alta capacidade de sequestro de carbono e grande importância ecológica.';
    }
    
    setProjeto(mockProjeto);
    
    // Mock de empresas compradoras com detalhes expandidos
    const mockEmpresas: EmpresaDetalhes[] = [
      {
        id: 'EMP-2023-001',
        nome: 'Natura Cosméticos',
        setor: 'Cosméticos',
        creditosAdquiridos: 3000,
        ultimaCompra: '2023-10-15',
        cnpj: '71.673.990/0001-77',
        endereco: 'Av. Alexandre Colares, 1188 - São Paulo, SP',
        telefone: '(11) 4446-2000',
        email: 'contato@natura.com.br',
        website: 'www.natura.com.br',
        historico: [
          {
            data: '2023-10-15',
            creditos: 1200,
            valor: 60000,
            projeto: 'Reflorestamento Vale do Ribeira'
          },
          {
            data: '2023-07-22',
            creditos: 800,
            valor: 40000,
            projeto: 'Reflorestamento Vale do Ribeira'
          },
          {
            data: '2023-04-05',
            creditos: 1000,
            valor: 45000,
            projeto: 'Reflorestamento Vale do Ribeira'
          }
        ]
      },
      {
        id: 'EMP-2023-002',
        nome: 'Banco Itaú',
        setor: 'Financeiro',
        creditosAdquiridos: 4500,
        ultimaCompra: '2023-11-22',
        cnpj: '60.701.190/0001-04',
        endereco: 'Praça Alfredo Egydio de Souza Aranha, 100 - São Paulo, SP',
        telefone: '(11) 3003-4828',
        email: 'sustentabilidade@itau.com.br',
        website: 'www.itau.com.br',
        historico: [
          {
            data: '2023-11-22',
            creditos: 2500,
            valor: 125000,
            projeto: 'Reflorestamento Vale do Ribeira'
          },
          {
            data: '2023-08-14',
            creditos: 2000,
            valor: 90000,
            projeto: 'Reflorestamento Vale do Ribeira'
          }
        ]
      },
      {
        id: 'EMP-2023-003',
        nome: 'Ambev',
        setor: 'Bebidas',
        creditosAdquiridos: 2800,
        ultimaCompra: '2023-09-05',
        cnpj: '07.526.557/0001-00',
        endereco: 'Rua Dr. Renato Paes de Barros, 1017 - São Paulo, SP',
        telefone: '(11) 2122-1200',
        email: 'sustentabilidade@ambev.com.br',
        website: 'www.ambev.com.br',
        historico: [
          {
            data: '2023-09-05',
            creditos: 1500,
            valor: 75000,
            projeto: 'Energia Solar Sertão Nordestino'
          },
          {
            data: '2023-06-18',
            creditos: 1300,
            valor: 65000,
            projeto: 'Energia Solar Sertão Nordestino'
          }
        ]
      },
      {
        id: 'EMP-2023-004',
        nome: 'Magazine Luiza',
        setor: 'Varejo',
        creditosAdquiridos: 1200,
        ultimaCompra: '2023-12-08',
        cnpj: '47.960.950/0001-21',
        endereco: 'Rua Voluntários da Franca, 1465 - Franca, SP',
        telefone: '(16) 3711-7000',
        email: 'sustentabilidade@magazineluiza.com.br',
        website: 'www.magazineluiza.com.br',
        historico: [
          {
            data: '2023-12-08',
            creditos: 800,
            valor: 40000,
            projeto: 'Preservação Manguezais Costeiros'
          },
          {
            data: '2023-09-30',
            creditos: 400,
            valor: 20000,
            projeto: 'Preservação Manguezais Costeiros'
          }
        ]
      }
    ];
    
    // Filtrando empresas compradoras relacionadas a este projeto
    if (id === 'PRJ-2023-001') {
      setEmpresasCompradoras([mockEmpresas[0], mockEmpresas[1]]);
    } else if (id === 'PRJ-2023-002') {
      setEmpresasCompradoras([mockEmpresas[2]]);
    } else if (id === 'PRJ-2023-003') {
      setEmpresasCompradoras([mockEmpresas[3], mockEmpresas[0]]);
    }
  }, [id]);
  
  const formatarData = (dataString: string): string => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  const formatarValor = (valor: number): string => {
    return valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };
  
  const abrirDetalhesEmpresa = (empresa: EmpresaDetalhes) => {
    setEmpresaSelecionada(empresa);
    setModalAberto(true);
  };
  
  if (!projeto) {
    return <div className="flex justify-center items-center h-screen">Carregando...</div>;
  }
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Cabeçalho */}
      <div className="bg-white px-6 py-4 border-b border-gray-200">
        <div className="container mx-auto">
          <div className="flex items-center mb-4">
            <button 
              className="mr-4 p-2 rounded-full hover:bg-gray-100"
              onClick={() => navigate('/pregao-dashboard')}
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Detalhes do Projeto <span className="text-[#0e7cb4]">Código: {projeto.id}</span></h1>
              <p className="text-gray-600">Visualização detalhada do projeto e empresas compradoras associadas</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Conteúdo Principal */}
      <div className="flex-grow p-6 bg-gray-50">
        <div className="container mx-auto">
          {/* Detalhes do Projeto */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row">
                <div className="flex-grow">
                  <div className="flex items-center mb-4">
                    <div className="rounded-full p-3 bg-[#0e7cb4]/10 mr-3">
                      <TreePine className="h-6 w-6 text-[#0e7cb4]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{projeto.nome}</h2>
                      <div className={`px-3 py-1 mt-1 rounded-full text-xs inline-flex items-center ${
                        projeto.statusVerificacao === 'Verificado' ? 'bg-green-100 text-green-800' :
                        projeto.statusVerificacao === 'Em análise' ? 'bg-amber-100 text-amber-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {projeto.statusVerificacao}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{projeto.descricao}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Leaf className="h-5 w-5 text-green-600 mr-2" />
                        <div>
                          <div className="text-sm text-gray-500">Tipo de Projeto</div>
                          <div className="font-medium">{projeto.tipo}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <MapPin className="h-5 w-5 text-[#0e7cb4] mr-2" />
                        <div>
                          <div className="text-sm text-gray-500">Região</div>
                          <div className="font-medium">{projeto.regiao}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <BadgeDollarSign className="h-5 w-5 text-[#0e7cb4] mr-2" />
                        <div>
                          <div className="text-sm text-gray-500">Créditos Gerados</div>
                          <div className="font-medium">{projeto.creditosGerados.toLocaleString('pt-BR')} KgCO2e</div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Calendar className="h-5 w-5 text-[#0e7cb4] mr-2" />
                        <div>
                          <div className="text-sm text-gray-500">Início do Projeto</div>
                          <div className="font-medium">{formatarData(projeto.dataInicio)}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Clock className="h-5 w-5 text-[#0e7cb4] mr-2" />
                        <div>
                          <div className="text-sm text-gray-500">Término Previsto</div>
                          <div className="font-medium">{formatarData(projeto.dataFim)}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center">
                        <Gavel className="h-5 w-5 text-[#0e7cb4] mr-2" />
                        <div>
                          <div className="text-sm text-gray-500">Participar do Pregão</div>
                          <Button 
                            className="mt-1 bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                            onClick={() => navigate('/pregao-lances/PG-2025-002')}
                          >
                            Acessar Pregão
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Empresas Compradoras */}
          <h2 className="text-xl font-semibold mb-4">Empresas Compradoras (compensação voluntária)</h2>
          {empresasCompradoras.length > 0 ? (
            <div className="mb-8 overflow-hidden">
              <Card>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Empresa
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Setor
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Créditos Adquiridos
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Última Compra
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {empresasCompradoras.map((empresa) => (
                        <tr key={empresa.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-[#0e7cb4]/10 rounded-md">
                                <Building className="h-5 w-5 text-[#0e7cb4]" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{empresa.nome}</div>
                                <div className="text-sm text-gray-500">{empresa.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{empresa.setor}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{empresa.creditosAdquiridos.toLocaleString('pt-BR')} KgCO2e</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{new Date(empresa.ultimaCompra).toLocaleDateString('pt-BR')}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[#0e7cb4] hover:bg-[#0e7cb4]/10"
                              onClick={() => abrirDetalhesEmpresa(empresa)}
                            >
                              Detalhes
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          ) : (
            <Card className="mb-8">
              <CardContent className="p-6 text-center text-gray-500 py-8">
                Não há empresas compradoras associadas a este projeto no momento.
              </CardContent>
            </Card>
          )}
          
          {/* Estatísticas do Projeto */}
          <h2 className="text-xl font-semibold mb-4">Estatísticas do Projeto</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardContent className="p-6 flex items-center">
                <div className="rounded-full p-4 bg-white shadow-sm mr-4">
                  <DollarSign className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Negociado</p>
                  <h3 className="text-2xl font-bold">
                    {empresasCompradoras.reduce((total, empresa) => total + empresa.creditosAdquiridos, 0).toLocaleString('pt-BR')} KgCO2e
                  </h3>
                  <p className="text-xs text-gray-500">
                    {((empresasCompradoras.reduce((total, empresa) => total + empresa.creditosAdquiridos, 0) / projeto.creditosGerados) * 100).toFixed(1)}% do total gerado
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 flex items-center">
                <div className="rounded-full p-4 bg-white shadow-sm mr-4">
                  <Building className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Empresas Participantes</p>
                  <h3 className="text-2xl font-bold">{empresasCompradoras.length}</h3>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 flex items-center">
                <div className="rounded-full p-4 bg-white shadow-sm mr-4">
                  <BadgeDollarSign className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Créditos Disponíveis</p>
                  <h3 className="text-2xl font-bold">
                    {(projeto.creditosGerados - empresasCompradoras.reduce((total, empresa) => total + empresa.creditosAdquiridos, 0)).toLocaleString('pt-BR')} KgCO2e
                  </h3>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Modal de Detalhes da Empresa */}
      {modalAberto && empresaSelecionada && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <div className="rounded-full p-3 bg-[#0e7cb4]/10 mr-3">
                    <Building className="h-6 w-6 text-[#0e7cb4]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{empresaSelecionada.nome}</h2>
                    <p className="text-sm text-gray-500">{empresaSelecionada.id}</p>
                  </div>
                </div>
                <button 
                  className="p-2 rounded-full hover:bg-gray-100"
                  onClick={() => setModalAberto(false)}
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Tag className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">CNPJ</div>
                      <div className="font-medium">{empresaSelecionada.cnpj}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Endereço</div>
                      <div className="font-medium">{empresaSelecionada.endereco}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Building className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Setor</div>
                      <div className="font-medium">{empresaSelecionada.setor}</div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Telefone</div>
                      <div className="font-medium">{empresaSelecionada.telefone}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Email</div>
                      <div className="font-medium">{empresaSelecionada.email}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <Globe className="h-5 w-5 text-[#0e7cb4] mr-2" />
                    <div>
                      <div className="text-sm text-gray-500">Website</div>
                      <div className="font-medium">{empresaSelecionada.website}</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="font-semibold text-lg flex items-center mb-4">
                  <History className="h-5 w-5 text-[#0e7cb4] mr-2" />
                  Histórico de Transações
                </h3>
                
                {empresaSelecionada.historico && empresaSelecionada.historico.length > 0 ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Data
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Projeto
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Créditos
                          </th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Valor
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {empresaSelecionada.historico.map((transacao, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {formatarData(transacao.data)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {transacao.projeto}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {transacao.creditos.toLocaleString('pt-BR')} KgCO2e
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {formatarValor(transacao.valor)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">
                    Não há transações registradas para esta empresa.
                  </p>
                )}
              </div>
              
              <div className="mt-6 flex justify-center">
                <div className="bg-[#0e7cb4]/10 rounded-lg p-4 flex items-center">
                  <CircleDollarSign className="h-6 w-6 text-[#0e7cb4] mr-3" />
                  <div>
                    <div className="text-sm text-gray-700">Total de créditos adquiridos</div>
                    <div className="text-xl font-bold text-[#0e7cb4]">
                      {empresaSelecionada.creditosAdquiridos.toLocaleString('pt-BR')} KgCO2e
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <Button 
                  className="bg-[#0e7cb4] hover:bg-[#0a5c87] text-white"
                  onClick={() => setModalAberto(false)}
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ProjetoOfertanteDetalhe; 